// Shared legend-preview computation used by CreatePage and the referral/event map
// builders so every surface renders an identical live legend. Extracted verbatim from
// CreatePage's legendPreview memo so the two never drift.

export const formatDurationHMS = (seconds) => {
  if (!seconds || seconds < 0) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);
  return parts.join(' ');
};

// Build the live legend preview object from a Strava activity (+ optional segment
// override) and the user's title/unit choices. Returns null when there is no activity.
export function buildLegendPreview(activity, { customTitle = '', distanceUnit = 'metric', segment = null } = {}) {
  if (!activity) return null;

  const isImperial = distanceUnit === 'imperial';
  const isRun = activity.type?.toLowerCase().includes('run');

  // Use segment stats when a segment is selected, otherwise the whole activity.
  const source = segment ? {
    distance: segment.distance,
    moving_time: segment.moving_time,
    average_watts: segment.average_watts,
    average_speed: segment.average_speed,
  } : {
    distance: activity.distance,
    moving_time: activity.moving_time,
    average_watts: activity.average_watts,
    average_speed: activity.average_speed,
  };

  const title = (customTitle || '').trim() || activity.name;
  const typeLabel = (activity.type || 'Activity').toUpperCase();

  const distanceKm = source.distance ? source.distance / 1000 : 0;
  const distanceVal = isImperial ? (distanceKm * 0.621371).toFixed(1) : distanceKm.toFixed(1);
  const distUnit = isImperial ? 'mi' : 'km';

  const duration = formatDurationHMS(source.moving_time);

  // Elevation always comes from the activity, never the segment.
  let elevVal = null;
  const elevUnit = isImperial ? 'ft' : 'm';
  if (activity.total_elevation_gain) {
    elevVal = isImperial
      ? Math.round(activity.total_elevation_gain * 3.28084).toString()
      : Math.round(activity.total_elevation_gain).toString();
  }

  let powerOrPace = null;
  if (isRun) {
    if (source.average_speed && source.average_speed > 0) {
      let paceSecondsPerKm = (1 / source.average_speed) * 1000;
      if (isImperial) paceSecondsPerKm *= 1.60934;
      const paceMin = Math.floor(paceSecondsPerKm / 60);
      const paceSec = Math.floor(paceSecondsPerKm % 60);
      powerOrPace = {
        type: 'pace',
        value: `${paceMin.toString().padStart(2, '0')}:${paceSec.toString().padStart(2, '0')}`,
        unit: isImperial ? '/mi' : '/km',
      };
    }
  } else if (source.average_watts) {
    powerOrPace = {
      type: 'power',
      value: Math.round(source.average_watts).toString(),
      unit: 'W',
    };
  }

  return { title, typeLabel, distance: distanceVal, distanceUnit: distUnit, duration, elevation: elevVal, elevationUnit: elevUnit, powerOrPace };
}
